<template>
    <UHeader
        v-model:open="isMenuOpen"
        :toggle="{
      color: 'primary',
      variant: 'ghost',
      icon: 'i-lucide-menu',
    }"
        title="Sauerland Spielgeräte"
        to="/"
    >
        <template #title>
            <NuxtPicture
                :class="[isScrolled ? 'h-7 md:h-8' : 'h-7 md:h-10', 'transition-all duration-300 ease-in-out']"
                :img-attrs="{ class: [isScrolled ? 'h-7 md:h-8' : 'h-7 md:h-10', 'w-auto transition-all duration-300 ease-in-out'] }"
                alt="Sauerland Spielgeräte"
                src="/logo-sauerland-spielgeraete.png"
            />
        </template>

        <template #right>
            <div class="nav-utility-icons flex items-center gap-2 shrink-0">
                <GlobalSearchNew size="md"/>
                <div class="relative">
                    <UButton
                        :to="{ name: 'wishlist' }"
                        color="secondary"
                        icon="lucide:star"
                        size="md"
                        square
                        title="Mein Merkzettel"
                        variant="subtle"
                    />
                    <client-only>
                            <span
                                v-if="wishlistItemCount > 0"
                                class="absolute -top-1 -right-1 rounded-full bg-primary text-white text-xs w-4 h-4 flex items-center justify-center leading-none pointer-events-none"
                            >{{ wishlistItemCount }}</span>
                    </client-only>
                </div>
                <UButton
                    v-if="status === 'authenticated'"
                    color="secondary"
                    icon="lucide:user"
                    size="md"
                    square
                    title="Abmelden"
                    variant="subtle"
                    @click="signOut()"
                />
                <UButton
                    v-else
                    :to="{ name: 'login' }"
                    color="secondary"
                    icon="lucide:user"
                    size="md"
                    square
                    title="Anmelden"
                    variant="subtle"
                />
            </div>
        </template>

        <UNavigationMenu
            :items="navigationItems"
            color="neutral"
            variant="pill"
        />

        <template #body>
            <!-- Mobile utility links: Contact, Appointment, Social -->
            <div class="flex flex-col gap-4 px-4 py-6 border-b">
                <UButton
                    block
                    color="primary"
                    icon="lucide:user-search"
                    size="xl"
                    variant="outline"
                    @click="openContactPerson"
                >
                    Ansprechpartner suchen
                </UButton>
                <UButton
                    block
                    color="secondary"
                    icon="lucide:calendar"
                    size="xl"
                    variant="outline"
                    @click="openContactPerson"
                >
                    Termin vereinbaren
                </UButton>
                <div class="flex justify-center gap-3">
                    <UButton
                        icon="mdi:facebook"
                        size="xl"
                        square
                        target="_blank"
                        to="https://www.facebook.com/sauerlandspielgeraete"
                        variant="ghost"
                    />
                    <UButton
                        icon="mdi:youtube"
                        size="xl"
                        square
                        target="_blank"
                        to="https://www.youtube.com/channel/UCwLjh7t2vkx5LJkF0hVEl6g"
                        variant="ghost"
                    />
                    <UButton
                        icon="lucide:instagram"
                        size="xl"
                        square
                        target="_blank"
                        to="https://www.instagram.com/sauerland_spielgeraete/"
                        variant="ghost"
                    />
                </div>
            </div>

            <UNavigationMenu
                :items="navigationItems"
                class="-mx-2.5"
                color="neutral"
                orientation="vertical"
                variant="link"
            />
        </template>
    </UHeader>
</template>

<script lang="ts" setup>
import type {NavigationMenuItem} from '@nuxt/ui';
import {computed, ref} from 'vue';
import {useWindowScroll} from '@vueuse/core';
import {StringTools} from '~~/utils/StringTools';
import {useWishlistStore} from '~~/stores/wishlist';

type MainNavCategory = {
    _id: string | number
    name: string
}

type MainCategoryEdge = {
    node?: {
        _id?: string | number | null
        name?: string | null
    } | null
}

type FindMainCategoriesResult = {
    categories?: {
        edges?: MainCategoryEdge[] | null
    } | null
}

const {
    status,
    signOut,
} = useAuth();
const store = useWishlistStore();
const wishlistItemCount = computed(() => store.itemCount);

const isMenuOpen = ref(false);

const emit = defineEmits<{
    'open-contact-person': []
}>();

function openContactPerson() {
    isMenuOpen.value = false;
    // Wait for drawer close animation before opening modal
    setTimeout(() => emit('open-contact-person'), 150);
}

const {
    data,
    pending,
    error,
} = await useAsyncGql<FindMainCategoriesResult>({
    operation: 'findMainCategories',
});

const mainNav = computed<MainNavCategory[]>(() => (data.value?.categories?.edges ?? [])
    .map((edge: { node: any; }) => edge.node)
    .filter((node: { _id?: string | number | null | undefined; name?: string | null | undefined; }): node is NonNullable<MainCategoryEdge['node']> => Boolean(node?._id && node?.name))
    .map((node: { _id: string | number; name: any; }) => ({
        _id: node._id as string | number,
        name: String(node.name),
    })));

const route = useRoute();

const materialRouteNames = [
    'material',
    'playgrounds',
    'slides',
    'melamini',
    'climbing',
    'waterFacilities',
    'metall',
];

const companyRouteNames = [
    'manufacturing',
    'service',
    'collaboration',
    'certification',
    'warranty',
    'downloads',
    'contact',
    'inclusion',
    'montage',
    'blog',
    'blog-post',
    'career',
];

const {y: scrollY} = useWindowScroll();
const isScrolled = computed(() => scrollY.value > 24);
const isCategoriesActive = computed(() => route.path.includes('kategorien') || route.path.includes('/c/'));
const isMaterialActive = computed(() => materialRouteNames.includes(String(route.name)));
const isCompanyActive = computed(() => companyRouteNames.includes(String(route.name)));

// Keep your existing source for mainNav/pending/error if it already exists in this component.
// This assumes mainNav is the category list used by the old mobile navigation.
const categoryChildren = computed<NavigationMenuItem[]>(() => {
    if (pending.value || error.value) {
        return [];
    }

    return mainNav.value.map((category) => ({
        label: category.name,
        to: {
            name: 'category',
            params: {
                id: String(category._id),
                name: StringTools.simplify(category.name),
            },
        },
    }));
});

const navigationItems = computed<NavigationMenuItem[]>(() => [
    {
        label: 'Produktwelt',
        icon: 'i-lucide-shapes',
        to: {name: 'categories'},
        active: isCategoriesActive.value,
        defaultOpen: isCategoriesActive.value,
        children: categoryChildren.value,
    },
    {
        label: '3D-Konfigurator',
        icon: 'i-lucide-rotate-3d',
        to: {name: 'playgroundConfigurator'},
        active: route.name === 'playgroundConfigurator',
        defaultOpen: route.name === 'playgroundConfigurator',
        children: [
            {
                label: 'Video: Einleitung',
                to: {
                    name: 'playgroundConfigurator',
                    hash: '#video-193',
                },
            },
            {
                label: 'Video: Technische Vorstellung',
                to: {
                    name: 'playgroundConfigurator',
                    hash: '#video-188',
                },
            },
        ],
    },
    {
        label: 'Material',
        icon: 'i-lucide-hammer',
        to: {name: 'material'},
        active: isMaterialActive.value,
        defaultOpen: isMaterialActive.value,
        children: [
            {
                label: 'Spiellandschaften',
                to: {name: 'playgrounds'},
            },
            {
                label: 'Rutschen',
                to: {name: 'slides'},
            },
            {
                label: 'Melamini',
                to: {name: 'melamini'},
            },
            {
                label: 'Kletterstrecken',
                to: {name: 'climbing'},
            },
            {
                label: 'Wasserspielanlagen',
                to: {name: 'waterFacilities'},
            },
            {
                label: 'Metall 4.0',
                to: {name: 'metall'},
            },
        ],
    },
    {
        label: 'Nachhaltigkeit',
        icon: 'i-lucide-leaf',
        to: {name: 'sustainability'},
        active: route.name === 'sustainability',
    },
    {
        label: 'AR-App',
        icon: 'i-lucide-scan-eye',
        to: {name: 'arApp'},
        active: route.name === 'arApp',
        defaultOpen: route.name === 'arApp',
        children: [
            {
                label: 'Video: Einleitung',
                to: {
                    name: 'arApp',
                    hash: '#video-459',
                },
            },
            {
                label: 'Video: Funktionen',
                to: {
                    name: 'arApp',
                    hash: '#video-458',
                },
            },
        ],
    },
    {
        label: 'Unternehmen',
        icon: 'i-lucide-badge-info',
        active: isCompanyActive.value,
        defaultOpen: isCompanyActive.value,
        children: [
            {
                label: 'Fertigung',
                to: {name: 'manufacturing'},
            },
            {
                label: 'Service',
                to: {name: 'service'},
            },
            {
                label: 'Wir für Sie',
                to: {name: 'collaboration'},
            },
            {
                label: 'Zertifizierung',
                to: {name: 'certification'},
            },
            {
                label: 'Garantie und Wartung',
                to: {name: 'warranty'},
            },
            {
                label: 'Downloads',
                to: {name: 'downloads'},
            },
            {
                label: 'Kontakt',
                to: {name: 'contact'},
            },
            {
                label: 'Inklusion',
                to: {name: 'inclusion'},
            },
            {
                label: 'Montage',
                to: {name: 'montage'},
            },
            {
                label: 'Blog',
                to: {name: 'blog'},
            },
            {
                label: 'Karriere',
                to: {name: 'career'},
            },
        ],
    },
]);
</script>
